import styled from "@emotion/styled";
import { gsap } from "gsap";
import PropTypes from "prop-types";
import React, { useEffect, useReducer, useRef, useState } from "react";
import ReactPlayer from "react-player";

import useMousePosition from "../../hooks/useMousePosition";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import theme from "../../theme";
import Text from "../Text";

const Wrapper = styled.div`
  position: fixed;
  top: -100px;
  left: ${theme.space("s")};
  flex: 0 1 100%;
  display: inline-flex;
  pointer-events: none;
  z-index: 5;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${theme.color("blue.light")};
  margin-right: ${theme.space("s")};
  transform-origin: 0% 100%;
`;

const Image = styled.div`
  width: 380px;
  height: 250px;
  margin: 0 auto;
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center;
`;

const PrevImage = styled(Image)`
  top: 0;
  left: 0;
  position: absolute;
`;

const TextWrapper = styled.div`
  position: relative;
  width: 300px;
  overflow: hidden;
`;

const PrevText = styled(Text)`
  position: absolute;
  top: 0;
  left: 0;
`;

const Player = styled(ReactPlayer)`
  & > video {
    object-fit: cover;
  }
`;
function PrinciplesItem({ image, video, text, ...props }) {
  const position = useMousePosition();
  const imgWrapperElement = useRef();
  const [initialAnimation, setInitialAnimation] = useState(true);
  const imgElement = useRef([]);
  const textElement = useRef([]);

  const prefersReducedMotion = usePrefersReducedMotion();

  const [imageData, setImageData] = useReducer(
    (state) => {
      return prefersReducedMotion
        ? { previous: null, current: video ? video : image }
        : {
            previous: state.previous === null ? image : state.current,
            current: video ? video : image,
          };
    },
    {
      current: null,
      previous: null,
    }
  );

  const [textData, setTextData] = useReducer(
    (state) => {
      return prefersReducedMotion
        ? { previous: "", current: text }
        : { previous: state.current, current: text };
    },
    {
      current: "",
      previous: "",
    }
  );

  useEffect(() => {
    setInitialAnimation(imageData.previous === null ? true : false);
  }, [imageData.previous]);

  useEffect(() => {
    if (!prefersReducedMotion) {
      if (imgElement.current) {
        const imgTimeline = gsap.timeline();
        imgTimeline
          .add(setImageData())
          .fromTo(
            imgElement.current[0],
            {
              x: initialAnimation ? 0 : -300,
              opacity: 0,
            },
            {
              x: 0,
              opacity: 1,
            },
            initialAnimation ? 0.2 : 0
          )
          .fromTo(
            imgElement.current[1],
            {
              x: 0,
              opacity: initialAnimation ? 0 : 1,
            },
            {
              x: 300,
              opacity: 0,
            },
            0
          );
      }
      if (textElement.current) {
        const textTimeline = gsap.timeline();
        textTimeline
          .add(setTextData())
          .fromTo(
            textElement.current[0],
            {
              x: initialAnimation ? -300 : -10,
              opacity: 0,
            },
            {
              x: 0,
              opacity: 1,
              duration: 0.5,
              ease: "power3.out",
            },
            0
          )
          .fromTo(
            textElement.current[1],
            {
              x: 0,
              opacity: 1,
            },
            {
              x: 10,
              opacity: 0,
              duration: 0.5,
              ease: "power3.out",
            },
            0
          );
      }
    } else {
      setImageData();
      setTextData();
    }
    // eslint-disable-next-line
  }, [image, text, imgElement, textElement, prefersReducedMotion]);

  useEffect(() => {
    const imgWrapperTimeline = gsap.timeline();
    imgWrapperTimeline.fromTo(
      imgWrapperElement.current,
      {
        scale: 0,
        opacity: 0,
      },
      {
        scale: 1,
        opacity: 1,
        duration: 0.5,
        ease: "power3.out",
      },
      0
    );
  }, []);

  return (
    <Wrapper
      {...props}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
    >
      <InnerWrapper>
        <ImageContainer ref={imgWrapperElement}>
          <Image
            ref={(el) => (imgElement.current[0] = el)}
            url={imageData.current}
          >
            {typeof imageData.current === "object" &&
              imageData.current !== null && (
                <Player
                  className={"videoPlayer"}
                  url={imageData.current.url}
                  width="100%"
                  height="100%"
                  muted
                  playing
                  loop
                />
              )}
          </Image>

          <PrevImage
            ref={(el) => (imgElement.current[1] = el)}
            url={imageData.previous}
          >
            {typeof imageData.previous === "object" &&
              imageData.previous !== null && (
                <Player
                  className={"videoPlayer"}
                  url={imageData.previous.url}
                  width="100%"
                  height="100%"
                  muted
                  playing
                  loop
                />
              )}
          </PrevImage>
        </ImageContainer>
        <TextWrapper>
          <Text ref={(el) => (textElement.current[0] = el)}>
            {textData.current}
          </Text>
          <PrevText ref={(el) => (textElement.current[1] = el)}>
            {textData.previous}
          </PrevText>
        </TextWrapper>
      </InnerWrapper>
    </Wrapper>
  );
}

export const principlesItem = {
  image: PropTypes.string,
  video: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
    alt: PropTypes.string,
    url: PropTypes.string,
    video: PropTypes.shape({
      streamingUrl: PropTypes.string,
      thumbJpg: PropTypes.string,
    }),
  }),
  title: PropTypes.string,
  text: PropTypes.string,
};

PrinciplesItem.propTypes = {
  ...principlesItem,
};

PrinciplesItem.defaultProps = {};

export default PrinciplesItem;
